import { faPaperPlane } from '@fortawesome/pro-regular-svg-icons/faPaperPlane';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { useFormik } from 'formik';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import Button from '../buttons/Button';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import TextArea from '../inputs/TextArea';
import TextInput from '../inputs/TextInput';
import { Box } from '../layout';
import { getCaptchaKey } from '../security/Recaptcha';
import { openToast } from '../toast/ToastContext';
import { Bold, SubHeading, Text } from '../typography';
import { CommentsWrapper, FaqContactForm, FaqContactFormGrid } from './FaqContact.style';
import { Sent } from './FaqForm.style';

const validationSchemaShape = {
  name: yup.string().required('Name is required'),
  email: yup.string().trim().email('Invalid email format').required('Email is required'),
  number: yup
    .string()
    .matches(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]{9,11}$/, 'Invalid phone number format')
    .required('Number is required'),
  comments: yup.string().trim().required('Please enter some comments'),
};

const validationSchema = yup.object().shape(validationSchemaShape);
const agentValidationSchema = yup
  .object()
  .shape({ ...validationSchemaShape, companyName: yup.string().required('Please enter your company name') });

interface FaqContactProps {
  agent?: boolean;
}

const FaqContact: React.FC<FaqContactProps> = ({ agent }) => {
  const [sent, setSent] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      number: '',
      comments: '',
      companyName: '',
    },
    validationSchema: agent ? agentValidationSchema : validationSchema,
    onSubmit: async (values) => {
      if (!sending) {
        setSending(true);

        try {
          const recaptcha = await getCaptchaKey('enquire');
          const body = {
            name: values.name,
            email: values.email,
            telephone: values.number,
            comments: values.comments,
            recaptcha,
            reason: 'FAQ',
            customData: agent
              ? {
                  companyName: values.companyName,
                }
              : undefined,
          };

          await axios(
            {
              method: 'POST',
              url: `${API_URL}/enquire`,
              data: body,
            },
            dispatch,
          );

          setSent(true);
        } catch {
          openToast({
            title: 'Error',
            message: "Something went wrong. We couldn't send your enquiry. Please try again!",
            type: 'error',
          });
        }

        setSending(false);
      }
    },
  });

  return (
    <Box
      data-testid="faq-contact-form"
      display="flex"
      flexDirection="column"
      alignItems="center"
      maxWidth={935}
      m="0 auto"
    >
      {!sent && (
        <>
          <SubHeading fontSize={30} mb={45} data-testid="faq-contact-form-header">
            Get in touch
          </SubHeading>
          <Bold fontSize={18} mb={5} align="center">
            We&apos;re sorry you can&apos;t find what you&apos;re looking for, but we&apos;d be more than happy to help!
          </Bold>
          <Text fontSize={18} align="center">
            Please complete the form below with your enquiry and we&apos;ll get back to you as soon as possible.
          </Text>
        </>
      )}
      <Box mt={40}>
        {sent ? (
          <Sent
            id="faq-form"
            data-testid="faq-form-sent"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
          >
            <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: 46 }} />
            <SubHeading textTransform="uppercase" mt={20} fontSize={28} fontWeight="600">
              Thanks!
            </SubHeading>
            <Text mt={20} align="center" fontSize={18}>
              Your enquiry has been sent, and a member of the team will be in touch shortly
            </Text>
          </Sent>
        ) : (
          <FaqContactForm onSubmit={formik.handleSubmit}>
            <FaqContactFormGrid agent={agent}>
              <TextInput
                placeholder="Name"
                name="name"
                value={formik.values.name}
                onValueChange={formik.handleChange('name')}
                error={formik.touched.name && formik.errors.name}
                testID="nameInput"
                borderColour="transparent"
              />
              <TextInput
                placeholder="Email"
                name="email"
                value={formik.values.email}
                onValueChange={formik.handleChange('email')}
                error={formik.touched.email && formik.errors.email}
                testID="emailInput"
                borderColour="transparent"
              />
              <TextInput
                placeholder="Phone"
                name="number"
                value={formik.values.number}
                onValueChange={formik.handleChange('number')}
                error={formik.touched.number && formik.errors.number}
                testID="numberInput"
                borderColour="transparent"
              />
              {agent && (
                <TextInput
                  placeholder="Company Name"
                  name="companyName"
                  value={formik.values.companyName}
                  onValueChange={formik.handleChange('companyName')}
                  error={formik.touched.companyName && formik.errors.companyName}
                  testID="companyNameInput"
                  borderColour="transparent"
                />
              )}
              <CommentsWrapper agent={agent}>
                <TextArea
                  placeholder="Comments"
                  name="comments"
                  value={formik.values.comments}
                  onValueChange={formik.handleChange('comments')}
                  rows={5}
                  testID="commentsInput"
                  error={formik.touched.comments && formik.errors.comments}
                  borderColour="none"
                />
              </CommentsWrapper>
            </FaqContactFormGrid>
            <Box maxWidth={240} m="0 auto 25px auto">
              <Button type="submit" loading={sending} testID="submitButton">
                Contact us
              </Button>
            </Box>
          </FaqContactForm>
        )}
      </Box>
    </Box>
  );
};

export default FaqContact;
